import { Company, NavMenuItem } from '@employ-engage/data';
import { ForwardedRef, forwardRef } from 'react';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { IconMenu } from '../icon-menu/icon-menu';
import {
  useAddARoleTour,
  useCreateANewTemplateTour,
  useGenerateAReportTour,
  useSetupCompanyTour,
} from '../hooks';
import { useTranslation } from 'react-i18next';
import { LanguagePicker } from '../language-picker/language-picker';
import { Combobox } from '../combobox/combobox';
import { Button } from '../button/button';
import InlineLoadingSpinner from '../inline-loading-spinner/inline-loading-spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '../avatar/avatar';
import { nanoid } from 'nanoid';
import { useQuery, useMutation } from '@apollo/client';
import { GET_COMPANIES_USER_BELONGS_TO, UPDATE_CURRENT_USER } from '../api';
import { useAuthUser } from '../contexts/auth-context-type';
import { useCurrentCompany } from '../contexts';

export interface AdminHeaderProps {
  title: string;
  isMainPage?: boolean;
}

export const AdminHeader = forwardRef(
  ({ title, isMainPage }: AdminHeaderProps, ref: ForwardedRef<any>) => {
    const { setupCompanyTourInstance } = useSetupCompanyTour();
    useCreateANewTemplateTour();
    useGenerateAReportTour();
    const { addARoleTourInstance } = useAddARoleTour();
    const { t } = useTranslation(['common']);
    const { currentCompany: company, refetchCompany: refetchCurrentCompany } =
      useCurrentCompany();
    const { data: { companiesUserBelongsTo = [] } = {}, loading: isLoading } =
      useQuery(GET_COMPANIES_USER_BELONGS_TO);
    const [updateCurrentUser] = useMutation(UPDATE_CURRENT_USER);
    const navigate = useNavigate();
    const { signout, user } = useAuthUser();
    const { pathname } = useLocation();

    const avatarMenuItems: NavMenuItem[] = [];

    const onChangeCompany = async (newCompany: Company) => {
      if (newCompany) {
        await updateCurrentUser({
          variables: { input: { currentCompanyId: newCompany.id } },
        });

        await refetchCurrentCompany();

        const newPathname = pathname
          .split('/')
          .map((part, index) => {
            if (index === 1) {
              return newCompany.friendlyCompanyId;
            }
            return part;
          })
          .join('/');

        // Force a full page reload to reset state
        window.location.reload();
        navigate(newPathname);
      }
    };

    avatarMenuItems.push({
      title: t('Logout'),
      onClick: () => {
        signout()
          .then(() => {
            navigate('/', { state: nanoid() });
          })
          .catch((error) => {
            console.error(error);
          });
      },
      iconClassName: 'fa-solid fa-right-from-bracket',
    });

    return (
      <header
        ref={ref}
        className="w-full h-[100px] pl-[37px] pr-[50px] py-[19.61px] bg-white justify-between items-center flex"
      >
        <div className="text-neutral-900 text-3xl font-semibold leading-[42.55px] w-[200px]">
          {title}
        </div>
        <div className="justify-start items-center gap-[20.26px] flex">
          <LanguagePicker />
          {!isMainPage && (
            <div className="flex items-center gap-2">
              <IconMenu
                headerText={t('How do I...')}
                icon={
                  <i
                    aria-hidden="true"
                    className="fa-solid fa-circle-question size-5"
                  />
                }
                menuItems={[
                  {
                    iconClassName: '',
                    onClick: setupCompanyTourInstance?.start,
                    title: t('Setup a new company'),
                  },
                  {
                    iconClassName: '',
                    onClick: addARoleTourInstance?.start,
                    title: t('Add a Role'),
                  },
                ]}
              />
            </div>
          )}
          {!isMainPage && (
            <div>
              {isLoading ? (
                <InlineLoadingSpinner />
              ) : (
                <Combobox
                  items={companiesUserBelongsTo}
                  additionalActions={[
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => {
                        navigate('/user/home');
                      }}
                      className="text-blue-500 hover:underline"
                    >
                      {t('Manage Companies')}
                    </Button>,
                  ]}
                  mapItem={(company) => ({
                    id: company.id,
                    name: company.name,
                  })}
                  selectedItem={company}
                  setSelectedItem={onChangeCompany}
                />
              )}
            </div>
          )}
          {user ? <Avatar user={user} menuItems={avatarMenuItems} /> : null}
          <div className="justify-start items-center gap-[21.27px] flex">
            <div className="flex-col justify-start items-start gap-[3.04px] inline-flex">
              <div className="text-neutral-900 text-base font-medium leading-normal">
                {user?.displayName || user?.email || (
                  <LoadingSpinner spinnerId="admin-header-loading" />
                )}
              </div>
              <div className="text-neutral-500 text-sm font-normal leading-snug">
                {t('User')}
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }
);

export default AdminHeader;
